import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import type { AppNotification, NotificationList } from '@aurora/contracts';
import { get, post, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { absoluteTime, relativeTime } from '../../lib/format';
import { Button, Skeleton } from '../../components/ui';
import { useToast } from '../../components/toast';

/**
 * The notification bell.
 *
 * Polled rather than pushed. A websocket would be lower latency, but it also
 * has to survive proxies, sleeping laptops and reconnection storms, and a
 * service desk does not need sub-minute delivery for "a ticket was assigned to
 * you". Polling is dropped while the tab is hidden, so a backgrounded tab costs
 * nothing.
 */

// The shapes of `GET /notifications` come from the shared contract the API is
// tested against: the bell's own copy once drifted from the server's and
// showed every notification as unread.

export const notificationsQueryKey = ['notifications'] as const;

const KIND_TONES: Record<string, string> = {
  'sla.breached': 'bg-critical',
  'sla.at_risk': 'bg-caution',
  'sla.escalation': 'bg-caution',
  escalation: 'bg-caution',
  'ticket.assigned': 'bg-accent',
  'ticket.replied': 'bg-informative',
};

export function NotificationBell() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const notifications = useQuery<NotificationList, ApiError>({
    queryKey: notificationsQueryKey,
    queryFn: () => get('/notifications'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: 1,
  });

  /**
   * Marks some read, or all of them (no ids).
   *
   * Applied to the list at once rather than after the round trip: somebody
   * who picks a notification is already on their way to the page it names,
   * and the bell should agree it has been read by the time they look back.
   */
  const markRead = useMutation<unknown, ApiError, number[] | undefined, { previous?: NotificationList }>({
    mutationFn: (ids) => post('/notifications/read', ids ? { ids } : {}),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: notificationsQueryKey });
      const previous = queryClient.getQueryData<NotificationList>(notificationsQueryKey);
      if (previous) {
        const marking = (notification: AppNotification) => !ids || ids.includes(notification.id);
        const newlyRead = previous.data.filter((notification) => !notification.read && marking(notification)).length;
        queryClient.setQueryData<NotificationList>(notificationsQueryKey, {
          data: previous.data.map((notification) =>
            marking(notification) ? { ...notification, read: true } : notification,
          ),
          meta: { unread: ids ? Math.max(0, previous.meta.unread - newlyRead) : 0 },
        });
      }
      return { previous };
    },
    onError: (error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(notificationsQueryKey, context.previous);
      toast.error(error);
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: notificationsQueryKey }),
  });

  const list = notifications.data?.data ?? [];
  const unread = notifications.data?.meta?.unread ?? 0;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          className="relative flex size-8 items-center justify-center rounded-control text-ink-muted transition-colors hover:bg-sunken hover:text-ink"
        >
          <Bell aria-hidden className="size-4" />
          {unread > 0 && (
            <span
              aria-hidden
              className="absolute end-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-critical px-1 text-[10px] leading-none font-semibold text-white"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          collisionPadding={12}
          aria-label="Notifications"
          className="animate-pop z-50 flex max-h-[min(32rem,calc(100vh-5rem))] w-[min(24rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-card border border-line bg-raised shadow-float"
        >
          <div className="flex items-center gap-2 border-b border-line px-3 py-2">
            <h2 className="text-reading font-semibold text-ink">Notifications</h2>
            {unread > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="ms-auto"
                loading={markRead.isPending && markRead.variables === undefined}
                onClick={() => markRead.mutate(undefined)}
              >
                <CheckCheck aria-hidden className="size-3.5" />
                Mark all read
              </Button>
            )}
          </div>

          <div className="scrollbar-slim min-h-0 flex-1 overflow-y-auto">
            {notifications.isPending ? (
              <div aria-hidden className="divide-y divide-line">
                {[0, 1, 2].map((index) => (
                  <div key={index} className="flex gap-2.5 px-3 py-2.5">
                    <Skeleton className="mt-1.5 size-2 rounded-full" />
                    <div className="flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notifications.isError ? (
              <div role="alert" className="space-y-2 px-3 py-6 text-center">
                <p className="text-body font-medium text-critical-ink">Couldn’t load notifications</p>
                <p className="text-mini text-ink-muted">{notifications.error.message}</p>
                <Button size="sm" onClick={() => void notifications.refetch()}>
                  Try again
                </Button>
              </div>
            ) : list.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <p className="text-body font-medium text-ink">You’re all caught up</p>
                <p className="mt-1 text-mini text-ink-muted">
                  Assignments, replies and deadline warnings will appear here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {list.map((notification) => (
                  <li key={notification.id}>
                    <NotificationRow
                      notification={notification}
                      onOpen={() => {
                        if (!notification.read) markRead.mutate([notification.id]);
                        setOpen(false);
                      }}
                      onMarkRead={() => markRead.mutate([notification.id])}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function NotificationRow({
  notification,
  onOpen,
  onMarkRead,
}: {
  notification: AppNotification;
  onOpen: () => void;
  onMarkRead: () => void;
}) {
  const content = (
    <>
      <span
        aria-hidden
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          notification.read ? 'bg-transparent' : (KIND_TONES[notification.kind] ?? 'bg-accent'),
        )}
      />
      <span className="min-w-0 flex-1">
        {/* Not by colour alone: the state is in the text as well. */}
        {!notification.read && <span className="sr-only-focusable">Unread: </span>}
        <span
          dir="auto"
          title={notification.title}
          className={cn('block truncate text-body text-ink', !notification.read && 'font-semibold')}
        >
          {notification.title}
        </span>
        {notification.body && (
          <span dir="auto" title={notification.body} className="mt-0.5 line-clamp-2 block text-mini text-ink-muted">
            {notification.body}
          </span>
        )}
        <span className="mt-0.5 block text-mini text-ink-subtle">
          <time dateTime={notification.created_at} title={absoluteTime(notification.created_at)}>
            {relativeTime(notification.created_at)}
          </time>
          {notification.actor_label && (
            <>
              {' · '}
              <span dir="auto">{notification.actor_label}</span>
            </>
          )}
        </span>
      </span>
    </>
  );

  const rowClasses = cn('flex w-full gap-2.5 px-3 py-2.5 text-start', !notification.read && 'bg-accent-soft/40');

  // A row that knows where it leads is a link to it; one that does not is
  // just a message, and does not pretend to be clickable.
  if (notification.url) {
    return (
      <Link to={notification.url} onClick={onOpen} className={cn(rowClasses, 'transition-colors hover:bg-sunken')}>
        {content}
      </Link>
    );
  }

  return (
    <div className={rowClasses}>
      {content}
      {!notification.read && (
        <Button size="icon" variant="ghost" className="size-7 shrink-0" aria-label="Mark as read" onClick={onMarkRead}>
          <Check aria-hidden className="size-3.5" />
        </Button>
      )}
    </div>
  );
}
